/** Libellés et traductions page d'accueil — changelog v15 → v16 */

import { regionLabelFr } from "./invelpsRegions";

const SECTOR_FR = {
  "Information Technology": "Technologies de l'Info.",
  "Consumer Discretionary": "Conso. Discrétionnaire",
  "Consumer Staples": "Conso. Courante",
  "Communication Services": "Services de Comm.",
  "Health Care": "Santé",
  "Real Estate": "Immobilier",
  Financials: "Finance",
  Industrials: "Industrie",
  Materials: "Matériaux",
  Utilities: "Services Pub.",
  Energy: "Énergie",
};

const FLOW_STATE_FR = {
  ACCUMULATION: "ACCUMULATION",
  DISTRIBUTION: "DISTRIBUTION",
  NEUTRAL_VOLATILE: "NEUTRE VOLATILE",
  ROTATION_BUY_SUPPORT: "ROT. SUPPORT",
  ROTATION_SELL_PRESSURE: "ROT. PRESSION",
  OUTFLOW: "SORTIE FLUX",
  LEADER_CONFIRMED: "LEADER CONFIRMÉ",
  LEADER_COOLING: "REFROIDISSEMENT",
};

const DECISION_SIGNAL_FR = {
  BUY: "ACHAT",
  STRONG_BUY: "FORT ACHAT",
  ACCUMULATE: "ACCUMULER",
  HOLD: "CONSERVER",
  SELL: "VENDRE",
  REDUCE: "RÉDUIRE",
  WATCH: "SURVEILLER",
  AVOID: "ÉVITER",
};

/** Tooltips v16 (sans noms de tables SQL). */
export const HOME_TOOLTIP_DEFAULTS = {
  tooltip_buy_rate:
    "Taux d'Achat = titres signal ACHAT / univers total × 100. Sélectif : seuls les titres cumulant fondamentaux solides, valorisation attractive ET momentum favorable passent — seuil ≥ 4 critères favorables sur 6.",
  tooltip_dynamique:
    "La région qui concentre le plus de Flux de Capitaux Nets (FCN). +216 bp signifie que les flux acheteurs dépassent les flux vendeurs de 216 bp en termes pondérés.",
  tooltip_alpha:
    "Surperformance vs benchmark (proxy MSCI World). Calcul : Perf. annuelle stratégie − Perf. annuelle benchmark. Ici +74.85% = Durable Moat +92.85% − benchmark +18%.",
  tooltip_regime:
    "Signal synthétique issu du FCN pondéré sur l'univers Invelps (~21 445 titres). Combine direction des flux et persistance. « Sélectif » = flux acheteurs concentrés sur peu de titres.",
  tooltip_marche:
    "Rendement moyen du marché mondial sur le dernier mois. Moyenne pondérée de l'ensemble des zones géographiques suivies par Invelps. Baromètre global — pas un indice de référence.",
  tooltip_regime_engine:
    "Phase ACCUMULATION : les grands investisseurs achètent discrètement. Signal précurseur d'une hausse potentielle. Coexiste avec un score décision neutre tant que les fondamentaux individuels ne sont pas tous confirmés (signal Wyckoff précurseur).",
  tooltip_decision_engine:
    "Score composite issu des signaux individuels sur l'univers. Ni clairement haussier ni baissier : approche prudente recommandée.",
  tooltip_buy_count:
    "Titres avec signal ACHAT : forte convergence des critères décisionnels. Filtrez par région en accumulation et marge de sécurité (MdS).",
  tooltip_watch_count:
    "Titres en surveillance : signal pas encore assez fort pour acheter ni vendre. À suivre pour un basculement vers ACHAT ou VENDRE.",
  tooltip_avoid_count:
    "Titres avec signaux négatifs : au moins 3 critères sur 6 défavorables. Le modèle recommande d'éviter ou d'alléger ces positions.",
  tooltip_market_flow:
    "Suit où vont les capitaux des grands institutionnels. FCN% = flux cumulé accumulé sur la période. Axe horizontal FPN (Flux Positifs Nets), vertical FCN. Taille des bulles = nombre de titres du secteur.",
  tooltip_top_picks:
    "Les titres les mieux notés avec signal ACHAT. IScore = score global /100. MdS = décote par rapport à la valeur intrinsèque estimée. EVA = ROIC moins WACC.",
  tooltip_indices:
    "Indices mondiaux. Signal basé sur MM50 et MM200 (MarketTrend). vs MM50 = écart à la moyenne 50 jours. vs MM200 = écart à la moyenne 200 jours.",
  tooltip_leaders:
    "Classement basé sur le rendement mensuel (Ret M-1), pas uniquement sur les flux. Séquence top-down : Région → Pays → Secteur → Capitalisation → Style.",
  tooltip_strategies:
    "Classement des approches quantitatives testées sur l'univers Invelps. Perf. FY = performance cumulée sur l'exercice.",
  tooltip_ou_investir:
    "Synthèse automatique des signaux de la page. Ce résumé analytique oriente la réflexion — ce n'est pas un conseil d'investissement personnalisé.",
  tooltip_strategie:
    '"Durable Moat" +92.85% FY (Jan-Avr 2026). Performance exceptionnelle sur 4 mois. Alpha généré : +74.85% vs benchmark. Les performances passées ne garantissent pas les résultats futurs.',
  tooltip_pulse:
    "FCN Global (6M) · Référence MSCI · Delta FPN mensuel. Courbe verte = flux nets cumulés institutionnels (FCN).",
};

/** Applique les tooltips v16 (priorité sur le cache SP / statique). */
export function mergeHomeCacheTooltips(cache = {}) {
  const out = { ...cache };
  for (const [k, v] of Object.entries(HOME_TOOLTIP_DEFAULTS)) {
    if (v) out[k] = v;
  }
  return out;
}

export function regionLabelHome(regionEn) {
  return regionLabelFr(regionEn) || regionEn || "—";
}

export function sectorLabelFr(sector) {
  if (!sector) return "";
  return SECTOR_FR[sector] || sector;
}

export function sectorShortFr(sector) {
  const full = sectorLabelFr(sector);
  return full
    .replace("Technologies de l'Info.", "Tech. Info.")
    .replace("Conso. Discrétionnaire", "Conso. Discr.")
    .replace("Conso. Courante", "Conso. Cour.")
    .replace("Services de Comm.", "Serv. Comm.");
}

export function flowStateLabelFr(state) {
  if (!state) return "—";
  return FLOW_STATE_FR[state] || String(state).replace(/_/g, " ");
}

export function flowStateShortFr(state) {
  const s = flowStateLabelFr(state);
  if (state === "ACCUMULATION") return "ACCUM.";
  if (state === "DISTRIBUTION") return "DISTRIB.";
  if (state === "ROTATION_BUY_SUPPORT") return "ROT.+";
  if (state === "NEUTRAL_VOLATILE") return "NEUTRE";
  if (state === "ROTATION_SELL_PRESSURE") return "ROT.-";
  return s.length > 12 ? s.slice(0, 10) + "." : s;
}

export function flowStateColor(state) {
  if (state === "ACCUMULATION") return "#6f8a5b";
  if (state === "DISTRIBUTION") return "#c6523f";
  if (state === "ROTATION_BUY_SUPPORT") return "#4a9080";
  if (state === "NEUTRAL_VOLATILE") return "#8899aa";
  if (state === "ROTATION_SELL_PRESSURE") return "#d28a52";
  return "#caa85e";
}

/** Signal indice : signal_mt (MarketTrend) avec repli vs50/vs200. */
export function normalizeIndexSignal(raw, vs50, vs200) {
  const s = String(raw || "").trim();
  const u = s.toUpperCase();

  if (u.includes("LÉG") || u.includes("LEGER")) {
    if (u.includes("BAISS") || u.includes("BEAR")) return "Légèrement baissier";
    return "Légèrement haussier";
  }
  if (u.includes("HAUSS") || u === "BULLISH" || u === "BULL" || u.includes("LEADER_CONFIRMED")) {
    return "Haussier";
  }
  if (u.includes("BAISS") || u === "BEARISH" || u === "BEAR" || u === "OUTFLOW") {
    return "Baissier";
  }
  if (u.includes("NEUT") || u === "NEUTRAL") return "Neutre";

  const v50 = vs50 != null && Number.isFinite(Number(vs50)) ? Number(vs50) : null;
  const v200 = vs200 != null && Number.isFinite(Number(vs200)) ? Number(vs200) : null;
  if (v50 != null && v200 != null) {
    if (v50 > 0 && v200 > 0) return "Haussier";
    if (v50 < 0 && v200 < 0) return "Baissier";
    if (v50 > 0 && v200 <= 0) return "Légèrement haussier";
    if (v50 <= 0 && v200 > 0) return "Légèrement baissier";
    return "Neutre";
  }
  if (v50 != null) return v50 > 0 ? "Légèrement haussier" : v50 < 0 ? "Légèrement baissier" : "Neutre";
  return "Neutre";
}

export function indexSignalDisplay(sig) {
  const n = normalizeIndexSignal(sig);
  if (n === "Haussier") return "▲ HAUSSIER";
  if (n === "Baissier") return "▼ BAISSIER";
  if (n === "Légèrement haussier") return "▲ LÉG. +";
  if (n === "Légèrement baissier") return "▼ LÉG. −";
  return "◈ NEUTRE";
}

export function indexSignalColor(sig) {
  const n = normalizeIndexSignal(sig);
  if (n === "Haussier" || n === "Légèrement haussier") return "#6f8a5b";
  if (n === "Baissier" || n === "Légèrement baissier") return "#c6523f";
  return "#caa85e";
}

export function decisionSignalFr(sig) {
  if (!sig) return "—";
  return DECISION_SIGNAL_FR[sig] || sig;
}

export function decisionSignalColor(sig) {
  const s = String(sig || "");
  if (["BUY", "STRONG_BUY"].includes(s)) return "#6f8a5b";
  if (["ACCUMULATE"].includes(s)) return "#caa85e";
  if (["SELL", "REDUCE", "AVOID"].includes(s)) return "#c6523f";
  return "#caa85e";
}

export function sectorScatterTooltip(d) {
  if (!d) return "";
  const etat = flowStateLabelFr(d.flow_state);
  const fcn = d.cnf_pct != null ? `${d.cnf_pct > 0 ? "+" : ""}${Number(d.cnf_pct).toFixed(0)} bp` : "—";
  const ret = d.ret != null ? `${d.ret > 0 ? "+" : ""}${Number(d.ret).toFixed(1)}%` : "—";
  return `${sectorLabelFr(d.sector)}\nÉtat : ${etat}\nFCN : ${fcn}\nRet M-1 : ${ret}`;
}

export const SCATTER_LEGEND = [
  { key: "ACCUMULATION", label: "ACCUM.", color: "#6f8a5b" },
  { key: "ROTATION_BUY_SUPPORT", label: "ROT.+", color: "#4a9080" },
  { key: "NEUTRAL_VOLATILE", label: "NEUTRE", color: "#8899aa" },
  { key: "DISTRIBUTION", label: "DISTRIB.", color: "#c6523f" },
];
